import { useState } from 'react';
import { API_URL } from 'src/common/utils/api/fetchWrapper';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

const FALLBACK_FILENAME = 'Meeting transcript.docx';

function filenameFromDisposition(header: string | null): string {
  if (!header) return FALLBACK_FILENAME;
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // fall through to the plain filename
    }
  }
  const asciiMatch = header.match(/filename="([^"]+)"/i);
  return asciiMatch ? asciiMatch[1] : FALLBACK_FILENAME;
}

export function useDownloadTranscript(eventId: string) {
  const [isDownloading, setIsDownloading] = useState(false);

  const download = async () => {
    setIsDownloading(true);
    try {
      const response = await fetch(`${API_URL}/calendar-events/${eventId}/transcript/download`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Transcript download failed with status ${response.status}`);
      }
      const blob = await response.blob();
      const filename = filenameFromDisposition(response.headers.get('Content-Disposition'));
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      notifyAndLogError(
        'Failed to download transcript',
        'An error occurred while downloading the transcript. Please try again later.',
        error,
        { eventId },
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return { download, isDownloading };
}
